import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShareFormModal = ({ isOpen, form, onClose }) => {
  const [copied, setCopied] = useState('');

  if (!isOpen || !form) return null;

  const baseUrl = window.location.origin;
  const formUrl = `${baseUrl}/${form?.id}`;
  const embedCode = `<iframe src="${formUrl}" width="100%" height="600" frameborder="0" style="border:none;border-radius:8px;"></iframe>`;
  const scriptEmbed = `<script src="${baseUrl}/embed.js" data-form-id="${form?.id}" async></script>`;

  const handleCopy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    } catch {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    }
  };

  const socialLinks = [
    {
      key: 'twitter',
      label: 'Twitter / X',
      icon: 'Twitter',
      color: 'hover:bg-sky-50 hover:border-sky-300 hover:text-sky-600',
      url: `https://twitter.com/intent/tweet?text=Fill+out+my+form:+${encodeURIComponent(form?.title)}&url=${encodeURIComponent(formUrl)}`
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      icon: 'Linkedin',
      color: 'hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(formUrl)}`
    },
    {
      key: 'facebook',
      label: 'Facebook',
      icon: 'Facebook',
      color: 'hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-600',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(formUrl)}`
    },
    {
      key: 'email',
      label: 'Email',
      icon: 'Mail',
      color: 'hover:bg-green-50 hover:border-green-300 hover:text-green-600',
      url: `mailto:?subject=${encodeURIComponent(`Fill out: ${form?.title}`)}&body=${encodeURIComponent(`Please fill out this form: ${formUrl}`)}`
    }
  ];

  const isPublished = form?.status === 'published';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Share2" size={18} className="text-primary" />
            </div>
            <div>
              <h3 className="text-base font-heading font-semibold text-foreground">Share Form</h3>
              <p className="text-xs text-muted-foreground line-clamp-1">{form?.title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth p-1 rounded-md hover:bg-muted"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Status notice */}
          {!isPublished && (
            <div className="flex items-start gap-3 bg-warning/10 border border-warning/20 rounded-lg p-3">
              <Icon name="AlertCircle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
              <p className="text-xs text-muted-foreground">
                This form is currently a <strong>draft</strong>. Publish it so respondents can access the link.
              </p>
            </div>
          )}

          {/* Direct Link */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Direct Link</label>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2 bg-muted/50 border border-border rounded-lg px-3 py-2 min-w-0">
                <Icon name="Link" size={14} className="text-muted-foreground flex-shrink-0" />
                <span className="text-sm text-muted-foreground truncate">{formUrl}</span>
              </div>
              <Button
                variant={copied === 'link' ? 'default' : 'outline'}
                size="sm"
                iconName={copied === 'link' ? 'Check' : 'Copy'}
                iconPosition="left"
                onClick={() => handleCopy(formUrl, 'link')}
                className="flex-shrink-0"
              >
                {copied === 'link' ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </div>

          {/* Social Share */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Share on Social</label>
            <div className="grid grid-cols-2 gap-2">
              {socialLinks.map((social) => (
                <a
                  key={social.key}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-2 px-3 py-2 border border-border rounded-lg text-sm text-muted-foreground transition-smooth ${social.color}`}
                >
                  <Icon name={social.icon} size={15} />
                  <span>{social.label}</span>
                </a>
              ))}
            </div>
          </div>

          {/* iFrame Embed */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Embed (iFrame)</label>
            <div className="bg-muted/50 border border-border rounded-lg p-3 font-mono text-xs text-muted-foreground break-all leading-relaxed">
              {embedCode}
            </div>
            <Button
              variant={copied === 'iframe' ? 'default' : 'outline'}
              size="sm"
              iconName={copied === 'iframe' ? 'Check' : 'Copy'}
              iconPosition="left"
              onClick={() => handleCopy(embedCode, 'iframe')}
              className="mt-2"
            >
              {copied === 'iframe' ? 'Copied!' : 'Copy iFrame Code'}
            </Button>
          </div>

          {/* Script Embed */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Embed (Script)</label>
            <div className="bg-muted/50 border border-border rounded-lg p-3 font-mono text-xs text-muted-foreground break-all leading-relaxed">
              {scriptEmbed}
            </div>
            <Button
              variant={copied === 'script' ? 'default' : 'outline'}
              size="sm"
              iconName={copied === 'script' ? 'Check' : 'Copy'}
              iconPosition="left"
              onClick={() => handleCopy(scriptEmbed, 'script')}
              className="mt-2"
            >
              {copied === 'script' ? 'Copied!' : 'Copy Script Code'}
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-border bg-muted/30">
          <a
            href={`/embed-settings?formId=${form?.id}`}
            className="text-xs text-primary hover:underline flex items-center gap-1"
          >
            <Icon name="Settings" size={13} />
            Advanced embed settings
          </a>
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShareFormModal;
